/* eslint-disable security/detect-non-literal-fs-filename */
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  DialogModalComponent,
  DialogModalData,
} from 'src/app/shared/components/molecules/dialog-modal/dialog-modal.component';
import { EditBusinessModalComponent } from 'src/app/shared/components/molecules/edit-business-modal/edit-business-modal.component';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';
import { BusinessSummaryResponseDTO } from 'src/app/shared/models/dtos/responses/business-summary-response-dto/business-summary-response-dto';
import { CreateReportModalComponent } from 'src/app/shared/components/molecules/create-report-modal/create-report-modal.component';
import { PointTransactionModalComponent } from 'src/app/shared/components/organisms/point-transaction-modal/point-transaction-modal.component';
import { UpdateVipModalComponent } from 'src/app/shared/components/organisms/update-vip-modal/update-vip-modal.component';
import { FamilyModalComponent } from 'src/app/control-panel/components/organisms/family-modal/family-modal.component';
import { ImageUploadModalComponent } from 'src/app/shared/components/molecules/image-upload-modal/image-upload-modal.component';
import {
  DialogModalSelectComponent,
  DialogModalSelectData,
} from 'src/app/shared/components/molecules/dialog-modal-select/dialog-modal-select.component';
import { DownloadReportModalComponent } from 'src/app/shared/components/molecules/download-report-modal/download-report-modal.component';
import { ReportType } from 'src/app/utils/reportType';
import { ProductInfoModalComponent } from 'src/app/control-panel/components/organisms/product-info-modal/product-info-modal.component';
import { ProductDTO } from 'src/app/shared/models/dtos/responses/product-dto/product-dto';
import { ProductCreatedModalComponent } from 'src/app/shared/components/molecules/product-created-modal/product-created-modal.component';
import { FormControl } from '@angular/forms';
import { RewardCreatedModalComponent } from 'src/app/shared/components/molecules/reward-created-modal/reward-created-modal.component';
import { DeleteRewardModalComponent } from 'src/app/shared/components/molecules/delete-reward-modal/delete-reward-modal.component';
import { RewardInfoModalComponent } from 'src/app/shared/components/molecules/reward-info-modal/reward-info-modal.component';
import { RewardResponseDTO } from 'src/app/shared/models/dtos/responses/reward-response-dto/reward-response-dto';
import { ProductRequestDTO } from 'src/app/shared/models/dtos/requests/product-request-dto/product-request-dto';
import { PromoTextModalComponent } from 'src/app/control-panel/components/organisms/promo-text-modal/promo-text-modal.component';
import { PromotionalTextDTO } from 'src/app/shared/models/dtos/responses/promotional-text-dto/promotional-text-dto';
import { VipLevelSettings } from 'src/app/shared/models/constants/vip-level-settings';
import { DeleteVipModalComponent } from 'src/app/shared/components/molecules/delete-vip-modal/delete-vip-modal.component';
import { AddVipModalComponent } from 'src/app/shared/components/molecules/add-vip-modal/add-vip-modal.component';
import { FamilyDTO } from 'src/app/shared/models/dtos/responses/family-dto/family-dto';
import { SettingsScheduleModalComponent } from 'src/app/shared/components/molecules/settings-schedule-modal/settings-schedule-modal.component';
import { BusinessUnificationDetailsModalComponent } from 'src/app/shared/components/molecules/business-unification-details-modal/business-unification-details-modal.component';
import { NewBusinessUnificationModalComponent } from 'src/app/shared/components/molecules/new-business-unification-modal/new-business-unification-modal.component';

@Injectable({
  providedIn: 'root',
})
/**
 * Friendly dialog wrapper
 */
export class DialogService {
  /**
   * @constructor
   */
  constructor(private dialog: MatDialog) {}

  /**
   * Opens a modal and can execute actions depending on the response
   * @param {DialogModalData} dialogData
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   */
  openDialog(
    dialogData: DialogModalData,
    acceptAction?: () => void,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(DialogModalComponent, {
      data: dialogData,
      autoFocus: false,
    });

    this.callResponseMethod(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens global demotion modal
   * @param {DialogModalData} dialogData what to do when modal is accepted
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   */
  openGlobalAdminDemotion(
    dialogData: DialogModalSelectData,
    acceptAction?: (selectedRegions: number[]) => void,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(DialogModalSelectComponent, {
      autoFocus: false,
      data: dialogData,
      panelClass: 'custom-modal-overlay',
    });

    dialogRef.afterClosed().subscribe((response: any) => {
      if (
        response?.result === MatDialogResponse.SUCCESS &&
        response.selectedRegions
      ) {
        if (acceptAction) {
          acceptAction(response.selectedRegions);
        }
      } else if (failAction) {
        failAction();
      }
    });
  }

  /**
   * Opens a modal that show business unification details
   * @param {number} businessUnificationId id of business unification to be detailed
   * @param {Function} acceptAction function called when modal is closed
   */
  openShowBusinessUnificationDetailsModal(
    businessUnificationId: number,
    acceptAction?: (response?: any) => any
  ) {
    const dialogRef = this.dialog.open(
      BusinessUnificationDetailsModalComponent,
      {
        width: '600px',
        height: '100vh',
        maxHeight: '100vh',
        position: {
          top: '0px',
          right: '0px',
        },
        data: {
          id: businessUnificationId,
        },
        autoFocus: false,
      }
    );

    dialogRef.afterClosed().subscribe((response: any) => {
      if (acceptAction) {
        acceptAction!(response);
      }
    });
  }

  /**
   * Opens a modal that shows a form to create a new business unification
   * @param {Function} acceptAction function called when modal is closed
   */
  openNewBusinessUnificationModal(acceptAction?: (response?: any) => any) {
    const dialogRef = this.dialog.open(NewBusinessUnificationModalComponent, {
      width: '580px',
      maxWidth: 580,
      data: {},
      autoFocus: false,
    });

    dialogRef.afterClosed().subscribe((response: any) => {
      if (response === MatDialogResponse.SUCCESS && acceptAction) {
        acceptAction!();
      }
    });
  }

  /**
   * Opens a modal that edit business data
   * @param {BusinessSummaryResponseDTO} businessSummary
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openEditBusinessModal(
    businessSummary: BusinessSummaryResponseDTO,
    acceptAction?: (response?: any) => any
  ) {
    const dialogRef = this.dialog.open(EditBusinessModalComponent, {
      minHeight: '750px',
      maxWidth: 580,
      data: businessSummary,
      autoFocus: false,
    });

    this.subscribeOneAction(dialogRef, acceptAction);
  }

  /**
   * Opens a modal that edit business data
   * @param {BusinessSummaryResponseDTO} businessSummary
   * @param {FamilyDTO} family
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openUpdateVipLevelModal(
    businessSummary: BusinessSummaryResponseDTO,
    family: FamilyDTO,
    acceptAction?: (response?: any) => any
  ) {
    const dialogRef = this.dialog.open(UpdateVipModalComponent, {
      panelClass: 'custom-dialog-container',
      data: {
        businessSummary: businessSummary,
        family: family,
      },
      autoFocus: false,
      maxWidth: 650,
    });

    this.subscribeOneAction(dialogRef, acceptAction);
  }

  /**
   * Opens a create a report modal
   * @param {any} data
   */
  openCreateReportModal(data: any) {
    this.dialog.open(CreateReportModalComponent, {
      data: data,
      minWidth: 380,
      autoFocus: false,
    });
  }

  /**
   * Opens transaction points modal
   * @param {any} data
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   */
  openTransactionPointModal(
    data: any,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(PointTransactionModalComponent, {
      data: data,
      autoFocus: false,
    });

    this.subscribeAcceptOrFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens image upload modal
   * @param {any} picture
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   */
  openUpdateImage(
    picture?: any,
    acceptAction?: (response: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(ImageUploadModalComponent, {
      data: picture,
      autoFocus: false,
    });

    this.subscribeAcceptOrFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens image upload modal
   * @param {FormControl} pictureControl
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   * @param {String} modalTitle translation key to set in modal title
   */
  openUpdateImageWithControl(
    pictureControl?: FormControl,
    acceptAction?: (response: any) => any,
    failAction?: () => void,
    modalTitle?: string
  ) {
    const dialogRef = this.dialog.open(ImageUploadModalComponent, {
      data: {
        control: pictureControl,
        modalTitle,
      },
      autoFocus: false,
    });

    this.subscribeAcceptOrFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens Download Reports Modal
   * @param {any} data
   * @param {Function} acceptAction
   * @param {Function} failAction
   */
  openDownloadReportsModal(
    data: ReportType,
    acceptAction: (result: {
      dateRange: { start: Date | null; end: Date | null } | null;
      option: string | null;
    }) => void,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(DownloadReportModalComponent, {
      data: data,
      autoFocus: false,
    });

    this.subscribeCheckFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens a modal that shows product data
   * @param {ProductDTO} data Product data
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openProductInfoModal(
    data: ProductDTO,
    acceptAction?: (response?: any) => any
  ) {
    this.dialog.open(ProductInfoModalComponent, {
      panelClass: 'control-panel-modal',
      autoFocus: false,
      data: {
        productData: data,
        acceptAction: acceptAction,
      },
    });
  }

  /**
   * Opens created product modal
   * @param {string} product Product name
   * @param {Function} acceptAction
   * @param {Function} failAction
   */
  openProductCreatedModal(
    product: ProductRequestDTO,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(ProductCreatedModalComponent, {
      panelClass: 'product-created-modal',
      autoFocus: false,
      data: product,
    });

    this.subscribeCheckFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens a modal confirming that the reward was created successfully.
   * @param {DialogModalData} data Modal data
   * @param {Function} acceptAction
   * @param {Function} failAction
   */
  openRewardCreatedModal(
    data: DialogModalData,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(RewardCreatedModalComponent, {
      autoFocus: false,
      data: data,
      hasBackdrop: true,
      panelClass: 'control-panel-modal',
    });

    this.subscribeCheckFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens a modal to confirm reward deletion
   * @param {RewardResponseDTO} data
   * @param {Function} acceptAction
   * @param {Function} failAction
   * @param {Function} deleteHandler Handler only called by the info modal, since it won't be able to
   * reference the rewards component deletion function directly
   */
  openDeleteRewardModal(
    data: RewardResponseDTO,
    acceptAction?: (response?: any) => any,
    failAction?: () => void,
    deleteHandler?: (reward: RewardResponseDTO) => void
  ) {
    const dialogRef = this.dialog.open(DeleteRewardModalComponent, {
      autoFocus: true,
      panelClass: 'control-panel-modal',
      data: data,
    });

    dialogRef.afterClosed().subscribe((response: any) => {
      if (response === undefined || response === MatDialogResponse.FAIL) {
        if (failAction) {
          failAction();
        }
      } else {
        if (deleteHandler) {
          deleteHandler(data);
        }
        if (acceptAction) {
          acceptAction(response);
        }
      }
    });
  }

  /**
   * Opens a modal to confirm vip deletion
   * @param {VipLevelSettings} data
   * @param {Function} acceptAction
   * @param {Function} failAction
   */
  openDeleteVipModal(
    data: VipLevelSettings,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(DeleteVipModalComponent, {
      autoFocus: false,
      panelClass: 'control-panel-modal',
      data: data,
    });

    this.subscribeUndefinedOrFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens a modal to confirm vip deletion
   * @param {number} data
   * @param {Function} acceptAction
   * @param {Function} failAction
   */
  openAddVipModal(
    data: {
      familyId: number;
      vipSeriesCount: number;
    },
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ) {
    const dialogRef = this.dialog.open(AddVipModalComponent, {
      autoFocus: false,
      panelClass: 'control-panel-modal',
      data: data,
    });

    this.subscribeUndefinedOrFail(dialogRef, acceptAction, failAction);
  }

  /**
   * Opens a modal that shows reward data and allows passing a removeReward callback.
   * @param {RewardResponseDTO} data Product data
   * @param {any[]} availableFamilies Available families
   * @param {any} availableBusinessLevels Available business levels
   * @param {Function} deleteHandler Callback to remove the reward (will be passed to the modal)
   */
  openRewardInfoModal(
    data: RewardResponseDTO,
    availableFamilies: any[],
    availableBusinessLevels: any,
    deleteHandler?: (reward: RewardResponseDTO) => void
  ) {
    this.dialog.open(RewardInfoModalComponent, {
      panelClass: 'control-panel-modal',
      autoFocus: false,
      data: {
        reward: data,
        availableFamilies: availableFamilies,
        availableBusinessLevels: availableBusinessLevels,
        deleteHandler: deleteHandler,
      },
    });
  }

  /**
   * Opens a modal that create and edit a family and vip settings
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openFamilyFormModal(acceptAction?: () => void): void {
    const dialogRef = this.dialog.open(FamilyModalComponent, {
      panelClass: 'control-panel-modal',
      autoFocus: false,
    });
    this.callResponseMethod(dialogRef, acceptAction);
  }

  /**
   * Opens a modal that edit Promotional text from coupon vip
   * @param {PromotionalTextDTO} vipConfigSeriesData the vip config series
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openPromoTextModal(
    vipConfigSeriesData: PromotionalTextDTO,
    acceptAction?: () => void
  ): void {
    const dialogRef = this.dialog.open(PromoTextModalComponent, {
      panelClass: 'control-panel-modal',
      data: vipConfigSeriesData,
      autoFocus: false,
    });
    this.callResponseMethod(dialogRef, acceptAction);
  }

  /**
   * Subscribes to dialog close and calls acceptAction if provided
   * @param {MatDialogRef} dialogRef reference to the dialog
   * @param {Function} acceptAction callback on any close
   */
  private subscribeOneAction(
    dialogRef: MatDialogRef<any>,
    acceptAction?: (response?: any) => any
  ): void {
    dialogRef.afterClosed().subscribe((response: any) => {
      if (acceptAction) {
        acceptAction(response);
      }
    });
  }

  /**
   * Subscribes to dialog close: calls acceptAction if truthy, failAction otherwise
   * @param {MatDialogRef} dialogRef reference to the dialog
   * @param {Function} acceptAction callback when response is truthy
   * @param {Function} failAction callback when response is falsy
   */
  private subscribeAcceptOrFail(
    dialogRef: MatDialogRef<any>,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ): void {
    dialogRef.afterClosed().subscribe((response: any) => {
      if (acceptAction) {
        acceptAction(response);
      } else if (failAction) {
        failAction();
      }
    });
  }

  /**
   * Subscribes to dialog close: calls acceptAction unless response is FAIL
   * @param {MatDialogRef} dialogRef reference to the dialog
   * @param {Function} acceptAction callback when response is not FAIL
   * @param {Function} failAction callback when response is FAIL
   */
  private subscribeCheckFail(
    dialogRef: MatDialogRef<any>,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ): void {
    dialogRef.afterClosed().subscribe((response: any) => {
      if (response !== MatDialogResponse.FAIL && acceptAction) {
        acceptAction(response);
      } else if (response === MatDialogResponse.FAIL && failAction) {
        failAction();
      }
    });
  }

  /**
   * Subscribes to dialog close: calls failAction when response is undefined or FAIL
   * @param {MatDialogRef} dialogRef reference to the dialog
   * @param {Function} acceptAction callback when response is a valid value
   * @param {Function} failAction callback when response is undefined or FAIL
   */
  private subscribeUndefinedOrFail(
    dialogRef: MatDialogRef<any>,
    acceptAction?: (response?: any) => any,
    failAction?: () => void
  ): void {
    dialogRef.afterClosed().subscribe((response: any) => {
      if (response === undefined || response === MatDialogResponse.FAIL) {
        if (failAction) {
          failAction();
        }
      } else {
        if (acceptAction) {
          acceptAction(response);
        }
      }
    });
  }

  /**
   * Opens a modal that edit Promotional text from coupon vip
   * @param {MatDialogRef} dialogRef the vip config series
   * @param {Function} acceptAction what to do when modal is accepted
   * @param {Function} failAction what to do when modal is refused
   */
  callResponseMethod(
    dialogRef: MatDialogRef<any>,
    acceptAction?: () => void,
    failAction?: () => void
  ): void {
    dialogRef.afterClosed().subscribe((dialogResponse: MatDialogResponse) => {
      if (dialogResponse === MatDialogResponse.SUCCESS && acceptAction) {
        acceptAction();
      } else if (dialogResponse === MatDialogResponse.FAIL && failAction) {
        failAction();
      }
    });
  }

  /**
   * Opens a modal that create and edit a family and vip settings
   * @param {any} _systemSettings
   * @param {Function} acceptAction what to do when modal is accepted
   */
  openSettingScheduleFormModal(
    _systemSettings: any,
    acceptAction?: () => void
  ): void {
    const dialogRef = this.dialog.open(SettingsScheduleModalComponent, {
      data: _systemSettings,
      autoFocus: false,
    });
    this.callResponseMethod(dialogRef, acceptAction);
  }
}
